<template>
  <dropdown placement="bottom-end" :offset="[0, 10]" class="app-header-dropdown-menu" title="Go To...">
    <div class="app-header-dropdown-menu__trigger">
      <avatar :size="32" :person="$permissions.loggedInUser" />
      <i class="ddi ddi-caret-down" />
    </div>
    <template #content>
      <div>
        <h5 v-if="$screen.isTabletOrDesktop($mq)">Go To...</h5>
        <ul>
          <!-- THEME TOGGLE -->
          <li @click="toggleTheme">
            <a class="theme-toggle">
              <span class="theme-icon">{{ isDarkTheme ? '☀️' : '🌙' }}</span>
              <span class="theme-text">{{ isDarkTheme ? 'Light mode' : 'Dark mode' }}</span>
            </a>
          </li>
          <!-- Account items -->
          <li v-if="$permissions.hasAdditionalAccounts" @click.prevent="RouteAssistant.goToBase()">
            <a :href="RouteAssistant.getProfileUrl(accountID)">
              <i class="ddi ddi-home" />
              <span>Switch accounts&hellip;</span>
            </a>
          </li>
          <li @click.prevent="RouteAssistant.goToProfile(accountID)">
            <a :href="RouteAssistant.getProfileUrl(accountID)">
              <i class="ddi ddi-profile" />
              <span>Your profile</span>
            </a>
          </li>
          <li v-if="$permissions.isAdminOrOwner" @click.prevent="RouteAssistant.goToPeople($router, accountID)">
            <router-link :to="RouteAssistant.getPeopleRoute(accountID)">
              <i class="ddi ddi-people" />
              <span>Users</span>
            </router-link>
          </li>
          <li v-if="$permissions.isAdminOrOwner && $permissions.isTieredPricingPlan" @click.prevent="RouteAssistant.goToGuests($router, accountID)">
            <router-link :to="RouteAssistant.getGuestsRoute(accountID)">
              <i class="ddi ddi-people" />
              <span>Guests</span>
            </router-link>
          </li>
          <li v-if="$permissions.isAdminOrOwner && $screen.isDesktop($mq)" @click.prevent="RouteAssistant.goToDevelopersHome($router, accountID)">
            <router-link :to="RouteAssistant.getDevelopersHomeRoute(accountID)">
              <i class="ddi ddi-cog" />
              <span>Developer Center</span>
            </router-link>
          </li>
          <li v-if="$permissions.isOwner" @click.prevent="RouteAssistant.goToBillingInformation(accountID)">
            <a :href="RouteAssistant.getBillingInformationUrl(accountID)">
              <i class="ddi ddi-billing" />
              <span>Account &amp; billing</span>
            </a>
          </li>
          <!-- Support items -->
          <li @click.prevent="RouteAssistant.goToUrl(helpDocsUrl, true)">
            <a :href="helpDocsUrl" target="_blank">
              <i class="ddi ddi-info" />
              <span>Help docs</span>
              <i class="ddi external-arrow ddi-arrow-right" />
            </a>
          </li> 
          <li @click.prevent="RouteAssistant.goToUrl(supportUrl, true)">
            <a :href="supportUrl" target="_blank">
              <i class="ddi ddi-support" />
              <span>Support</span>
              <i class="ddi external-arrow ddi-arrow-right" />
            </a>
          </li>
          <li class="scary-list-option" @click.prevent="RouteAssistant.goToUrl(signOutUrl)">
            <a :href="signOutUrl">
              <i class="ddi ddi-signout"  />
              <span>Sign out</span>
            </a>
          </li>
        </ul>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import config from '@/config'

import RouteAssistant from '@/helpers/route-assistant'

export default defineComponent({
  name: 'AppHeaderDropdownMenu',

  data() {
    return {
      RouteAssistant,
      signOutUrl: `${config.serverSideAppUrl}/security/logout`,
      helpDocsUrl: 'https://www.donedone.com/help-center/donedone-2-help-docs',
      supportUrl: 'https://www.donedone.com/help-center',
      isDarkTheme: localStorage.getItem('theme') === 'dark',
    }
  },

  computed: {
    accountID(): string {
      return this.$route.params.accountId
    },
  },

  methods: {
    toggleTheme() {
      this.isDarkTheme = !this.isDarkTheme
      document.documentElement.setAttribute(
        'data-theme',
        this.isDarkTheme ? 'dark' : 'light',
      )

      localStorage.setItem('theme', this.isDarkTheme ? 'dark' : 'light')
    },
    created() {
      this.isDarkTheme = localStorage.getItem('theme') === 'dark'
    },
  },
})
</script>

<style lang="scss">
.app-header-dropdown-menu {
  &__trigger {
    display: flex;
    align-items: center;
    gap: 6px;
    border-radius: 8px;
    width: fit-content;
    padding: 8px 4px;
    margin-right: -4px;

    &:hover {
      @include media-breakpoint-up(sm) {
        background-color: var(--color-background-header);
      }
    }

    > i {
      @include dropdown-caret-size-color;
    }
  }

  a,
  span,
  i {
    color: var(--color-text-primary);
  }

  .external-arrow {
    margin-left: 4px;
    top: -1px;
    color: var(--color-text-third);
    transform: rotate(-45deg);
    font-size: 10px;
  }

  .dropdown__popper {
    background-color: var(--color-background-header);

    @include media-breakpoint-up(md) {
      min-width: 200px;
    }

    h5 {
      color: var(--color-text-secondary);
    }

    ul li:hover {
      background-color: var(--dropdown-item-hover-bg);
    }
  }
  .action-sheet__wrapper {
    background-color: var(--color-background-header);
    h5 {
      color: var(--color-text-secondary);
    }

    ul li:hover {
      background-color: var(--dropdown-item-hover-bg);
    }
  }

  .ddi.ddi-close {
    html[data-theme='dark'] & {
      color: var(--color-background-header);
    }
  }

  .theme-toggle {
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  .theme-icon {
    font-size: 15px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: -2px;
    margin-right: 4px;
  }

  @media (min-width: 768px) {
    .theme-icon {
      font-size: 13px;
    }
  }
}
</style>
